import {
  QUALIFICATION_CODES,
  GENDER_QUALIFICATION_OPTIONS,
} from './../../../../shared/constants/qualification-code';
import { AidaQualifications } from './../../../../shared/services/aida/aida-payload/aida-payload.service';
import { Criteria } from './../../../../shared/interfaces/quota.interface';
import { AIDA_ELIGIBILITY_ERROR_MESSAGES } from '../../ai-delivery-assistant.const';
import { Constants } from '../../../../shared/constants/create-survey.constants';

interface ValidationResponse {
  valid: boolean;
  error?: string;
}

export class AidaQuotaCriteriaValidaor {
  constructor(
    private readonly _quotaPercentage: number,
    private readonly _quotaCriteria: Criteria,
    private readonly _qualificationCensusData: AidaQualifications,
    private readonly _countryCode: string
  ) {}

  public valid(): ValidationResponse {
    if (this._quotaCriteria.range_sets?.length) {
      return new AidaRangeCriteriaValidator(
        this._quotaCriteria,
        this._qualificationCensusData
      ).valid();
    }

    return new AidaMultiPunchCriteriaValidator(
      this._quotaPercentage,
      this._quotaCriteria,
      this._qualificationCensusData,
      this._countryCode
    ).valid();
  }
}

export class AidaMultiPunchCriteriaValidator {
  constructor(
    private readonly _quotaPercentage: number,
    private readonly _quotaCriteria: Criteria,
    private readonly _qualificationCensusData: AidaQualifications,
    private readonly _countryCode: string
  ) {}

  public valid(): ValidationResponse {
    const dbQualificationCode =
      this._qualificationCensusData[this._quotaCriteria.qualification_code]
        .dbQualificationCode;

    if (dbQualificationCode === QUALIFICATION_CODES.GENDER) {
      return new AidaGenderCriteriaValidator(
        this._quotaPercentage,
        this._quotaCriteria,
        this._qualificationCensusData
      ).valid();
    }

    if (dbQualificationCode === QUALIFICATION_CODES.REGION) {
      return new AidaRegionCriteriaValidator().valid();
    }

    if (dbQualificationCode === QUALIFICATION_CODES.DIVISION) {
      return new AidaDivisionCriteriaValidator(this._countryCode).valid();
    }

    return { valid: false };
  }
}

export class AidaRangeCriteriaValidator {
  constructor(
    private readonly _quotaCriteria: Criteria,
    private readonly _qualificationCensusData: AidaQualifications
  ) {}

  public valid(): ValidationResponse {
    const dbQualificationCode =
      this._qualificationCensusData[this._quotaCriteria.qualification_code]
        .dbQualificationCode;
    if (dbQualificationCode === QUALIFICATION_CODES.AGE) {
      return new AidaAgeCriteriaValidator(this._quotaCriteria).valid();
    }
    return { valid: false };
  }
}

export class AidaGenderCriteriaValidator {
  constructor(
    private readonly _quotaPercentage: number,
    private readonly _quotaCriteria: Criteria,
    private readonly _qualificationCensusData: AidaQualifications
  ) {}

  valid(): ValidationResponse {
    // filtering prefer not to say
    const preferNotToSayCriteria = this._quotaCriteria.condition_codes?.find(
      (code) =>
        code === GENDER_QUALIFICATION_OPTIONS.PREFER_NOT_TO_SAY.toString()
    );

    if (preferNotToSayCriteria) {
      // checking for census data if available
      const censusData = this._qualificationCensusData[
        this._quotaCriteria.qualification_code
      ]?.census.data?.find(
        (data) =>
          data.condition_code === GENDER_QUALIFICATION_OPTIONS.PREFER_NOT_TO_SAY
      );

      // check census value must less than equal to quota percentage for aida eligibility
      const isCensusValueValid =
        censusData?.rep_value && this._quotaPercentage <= censusData?.rep_value;

      if (!isCensusValueValid) {
        return {
          valid: isCensusValueValid,
          error: AIDA_ELIGIBILITY_ERROR_MESSAGES.PREFER_NOT_TO_SAY,
        };
      }

      return { valid: isCensusValueValid };
    }

    return { valid: true };
  }
}

export class AidaAgeCriteriaValidator {
  constructor(private readonly _quotaCriteria: Criteria) {}

  AIDA_ELIGIBLE_AGE_DIFFERENCE: number = 6;

  valid(): ValidationResponse {
    const isValid =
      !!this._quotaCriteria.range_sets?.length &&
      !!this._quotaCriteria.range_sets?.every(
        (range) => range.to! - range.from! >= this.AIDA_ELIGIBLE_AGE_DIFFERENCE
      );

    if (!isValid) {
      return {
        valid: isValid,
        error: AIDA_ELIGIBILITY_ERROR_MESSAGES.AGE_MINIMUM_RANGE,
      };
    }

    return { valid: isValid };
  }
}

export class AidaRegionCriteriaValidator {
  valid(): ValidationResponse {
    // for region in AIDA eligibility no checks for now, so directly returing true
    return { valid: true };
  }
}

export class AidaDivisionCriteriaValidator {
  constructor(private readonly _countryCode: string) {}

  valid(): ValidationResponse {
    if (this._countryCode === Constants.AUSTRALIA_SHORT_CODE) {
      return { valid: true };
    }
    return { valid: false };
  }
}
